<script setup lang="ts">
    import { computed, onBeforeUnmount, onMounted, ref, useTemplateRef, watch } from 'vue';

    const props = withDefaults(defineProps<{
        /** Whether the secondary pane is shown. When false only the primary slot is rendered. */
        open: boolean;
        /** Where the chosen width is remembered. Leave unset to forget it between visits. */
        storageKey?: string | null;
        /** How wide the secondary pane starts out, before anyone has dragged the divider. */
        defaultWidth?: number;
        /** The narrowest the secondary pane may become. */
        minSecondary?: number;
        /** How much room the primary pane always keeps, however far the divider is dragged. */
        minPrimary?: number;
        /** The widest the secondary pane may become. */
        maxSecondary?: number;
        /** Below this container width the panes stack instead of sitting side by side. */
        stackBelow?: number;
        /** Describes the secondary pane to screen readers, as "Resize {label}". */
        label?: string;
    }>(), {
        storageKey: null,
        defaultWidth: 560,
        minSecondary: 380,
        minPrimary: 480,
        maxSecondary: 1000,
        stackBelow: 1024,
        label: 'panel'
    });

    const container = useTemplateRef<HTMLElement>('container');
    const width = ref(readStoredWidth() ?? props.defaultWidth);
    const containerWidth = ref(0);
    const dragging = ref(false);

    // Whether the user has been given the width they last chose. The key is not always known when
    // this component is set up: the code set page only knows the library once it has loaded, and
    // passes null until then, so the first read finds nothing and falls back to the default. The
    // stored width is picked up when the key arrives, unless the divider has already been moved.
    const storedWidthApplied = ref(readStoredWidth() !== null);

    watch(() => props.storageKey, () => {
        if (storedWidthApplied.value)
            return;

        const stored = readStoredWidth();
        if (stored !== null) {
            width.value = stored;
            storedWidthApplied.value = true;
        }
    });

    /** Matches the divider's flex basis in the stylesheet below. */
    const dividerWidth = 9;

    // The panes only sit side by side when there is enough room for both to stay usable. The
    // container is measured rather than the window, so a page that does not span the full width
    // stacks at the point its own content would start to suffer.
    const stacked = computed(() => containerWidth.value > 0 && containerWidth.value < props.stackBelow);

    // The width actually used, which trails the stored width so that narrowing the window borrows
    // room from the secondary pane without discarding the width the user picked.
    const appliedWidth = computed(() => clampWidth(width.value));

    let observer: ResizeObserver | null = null;

    onMounted(() => {
        if (!container.value)
            return;

        observer = new ResizeObserver(() => {
            containerWidth.value = container.value?.clientWidth ?? 0;
        });
        observer.observe(container.value);
        containerWidth.value = container.value.clientWidth;
    });

    onBeforeUnmount(() => {
        observer?.disconnect();
        observer = null;
    });

    function readStoredWidth(): number | null {
        if (!props.storageKey)
            return null;

        const stored = Number(localStorage.getItem(props.storageKey));
        return Number.isFinite(stored) && stored > 0 ? stored : null;
    }

    function storeWidth() {
        // Only called when the user moves the divider, so what is on screen is now their own
        // choice. Recorded even when there is no key yet, so that a key arriving a moment later
        // does not replace it with the width from a previous visit.
        storedWidthApplied.value = true;

        if (props.storageKey)
            localStorage.setItem(props.storageKey, String(Math.round(width.value)));
    }

    /**
     * Keeps the secondary pane within its own limits and leaves the primary pane its minimum. The
     * primary pane wins when the container is too narrow to satisfy both, because the secondary
     * pane is the one being sized.
     */
    function clampWidth(value: number): number {
        if (containerWidth.value === 0)
            return value;

        const roomBesidePrimary = containerWidth.value - props.minPrimary - dividerWidth;
        const widest = Math.max(props.minSecondary, Math.min(props.maxSecondary, roomBesidePrimary));

        return Math.round(Math.min(Math.max(value, props.minSecondary), widest));
    }

    function onPointerDown(event: PointerEvent) {
        if (stacked.value)
            return;

        dragging.value = true;
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        event.preventDefault();
    }

    function onPointerMove(event: PointerEvent) {
        if (!dragging.value || !container.value)
            return;

        // The divider is dragged, so the pane is sized from the pointer to the container's right
        // edge. Reading the edge each time keeps this correct while the page scrolls or reflows.
        width.value = clampWidth(container.value.getBoundingClientRect().right - event.clientX);
        event.preventDefault();
    }

    function onPointerUp(event: PointerEvent) {
        if (!dragging.value)
            return;

        dragging.value = false;
        (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
        storeWidth();
    }

    function onKeydown(event: KeyboardEvent) {
        if (stacked.value)
            return;

        const step = event.shiftKey ? 64 : 16;

        // Left widens the secondary pane, matching the direction the divider itself would move.
        if (event.key === 'ArrowLeft')
            width.value = clampWidth(appliedWidth.value + step);
        else if (event.key === 'ArrowRight')
            width.value = clampWidth(appliedWidth.value - step);
        else if (event.key === 'Home')
            width.value = clampWidth(props.maxSecondary);
        else if (event.key === 'End')
            width.value = clampWidth(props.minSecondary);
        else
            return;

        storeWidth();
        event.preventDefault();
    }
</script>

<template>
    <div
        ref="container"
        class="split-pane h-100"
        :class="{ 'is-split': open && !stacked, 'is-dragging': dragging }">
        <div class="split-pane-primary h-100">
            <slot name="primary" />
        </div>

        <div
            v-if="open && !stacked"
            class="split-pane-divider"
            role="separator"
            tabindex="0"
            aria-orientation="vertical"
            :aria-label="`Resize ${label}`"
            :aria-valuenow="appliedWidth"
            :aria-valuemin="minSecondary"
            :aria-valuemax="maxSecondary"
            :title="`Drag to resize the ${label}`"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointercancel="onPointerUp"
            @keydown="onKeydown" />

        <div
            v-if="open"
            class="split-pane-secondary"
            :style="open && !stacked ? { flexBasis: `${appliedWidth}px` } : undefined">
            <div class="split-pane-secondary-content">
                <slot name="secondary" />
            </div>
        </div>
    </div>
</template>

<style scoped>
.split-pane {
    display: flex;
    flex-direction: column;
    align-items: stretch;
}

.split-pane.is-split {
    flex-direction: row;
}

/* Without this a wide grid or a long unbroken value stretches the pane instead of scrolling. */
.split-pane-primary {
    min-width: 0;
}

.split-pane.is-split > .split-pane-primary {
    flex: 1 1 0;
}

.split-pane-divider {
    flex: 0 0 9px;
    position: relative;
    cursor: col-resize;
    background-color: transparent;
    border: none;
    padding: 0;
    touch-action: none;
}

/* The visible rule is thinner than the area that responds to the pointer, which stays easy to hit. */
.split-pane-divider::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 4px;
    width: 1px;
    background-color: #dbdbdb;
}

.split-pane-divider:hover::before,
.split-pane-divider:focus-visible::before,
.split-pane.is-dragging > .split-pane-divider::before {
    background-color: #3273dc;
    width: 3px;
    left: 3px;
}

.split-pane-divider:focus-visible {
    outline: none;
}

/*
 * Side by side the secondary pane scrolls its own content, so a long form cannot make the page
 * taller than the primary pane beside it. Taking the content out of the flow is what stops it
 * driving the height: the pane is stretched to the primary pane instead.
 */
.split-pane.is-split > .split-pane-secondary {
    position: relative;
    flex-grow: 0;
    flex-shrink: 0;
    min-width: 0;
}

.split-pane.is-split > .split-pane-secondary > .split-pane-secondary-content {
    position: absolute;
    inset: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding-left: 0.75rem;
}

/* While dragging, the pointer owns the divider and must not select text it passes over. */
.split-pane.is-dragging {
    user-select: none;
}
</style>
